import { FREQUENCIES, isDebtName, typeTag, type BudgetResponse, type CategoryMonth, type TypeTag } from "@/lib/budget";

export type TrackKey = "income" | "savings" | "expense" | "debt";
export interface Concept { id: string; name: string; tag: TypeTag | null; planned: number; actual: number; unbudgeted?: boolean; note?: boolean }
export interface TrackCategory { id: string; name: string; planned: number; actual: number; concepts: Concept[]; flow?: { contributed: number; withdrawn: number } }
export interface TrackSection { key: TrackKey; title: string; realLabel: string; restLabel: string; planned: number; actual: number; cats: TrackCategory[]; flow?: { contributed: number; withdrawn: number } }

const META: Record<TrackKey, { title: string; realLabel: string; restLabel: string; type: CategoryMonth["type"] }> = {
  income: { title: "Ingresos", realLabel: "Recibido", restLabel: "DIF", type: "income" },
  savings: { title: "Ahorro e Inversión", realLabel: "Apartado neto", restLabel: "DIF", type: "transfer" },
  expense: { title: "Gastos", realLabel: "Gastado", restLabel: "DIF", type: "expense" },
  debt: { title: "Deudas", realLabel: "Pagado", restLabel: "DIF", type: "expense" },
};
const isSpend = (k: TrackKey) => k === "expense" || k === "debt";
const r2 = (n: number) => Math.round(n * 100) / 100;
const TAG_ORDER = { E: 0, C: 1, D: 2 } as const;

function concepts(b: BudgetResponse, c: CategoryMonth, only?: "debt" | "rest"): Concept[] {
  const items: Concept[] = b.items.filter((i) => i.category_id === c.id && (!only || (i.group === "debt") === (only === "debt")))
    .map((i) => ({ id: i.id, name: i.frequency && i.frequency !== "monthly" ? `${i.name} · ${FREQUENCIES.find((f) => f.value === i.frequency)?.label}` : i.name,
      tag: typeTag(i.group), planned: i.amount, actual: i.actual_month_amount }))
    .sort((a, z) => (a.tag === z.tag ? z.planned - a.planned : (a.tag ? TAG_ORDER[a.tag] : 3) - (z.tag ? TAG_ORDER[z.tag] : 3)));
  if (only === "debt") return items;
  if (Math.abs(c.adjustment) > 0.005) items.push({ id: `${c.id}-adj`, name: "Ajuste del mes", tag: "C", planned: c.adjustment, actual: 0 });
  const debtActual = only === "rest" ? b.items.filter((i) => i.category_id === c.id && i.group === "debt").reduce((s, i) => s + i.actual_month_amount, 0) : 0;
  const loose = r2(c.actual_month_amount - debtActual - items.reduce((s, i) => s + i.actual, 0));
  if (Math.abs(loose) > 0.005) items.push({ id: `${c.id}-loose`, name: "No presupuestado", tag: null, planned: 0, actual: loose, unbudgeted: true });
  (b.msi_lines ?? []).filter((l) => l.category_id === c.id)
    .forEach((l, i) => items.push({ id: `${c.id}-msi-${i}`, name: l.label, tag: "D", planned: 0, actual: l.amount, note: true }));
  return items;
}

const sumItems = (b: BudgetResponse, c: CategoryMonth, k: "amount" | "actual_month_amount") =>
  r2(b.items.filter((i) => i.category_id === c.id && i.group === "debt").reduce((s, i) => s + i[k], 0));

function spendCats(b: BudgetResponse, cats: CategoryMonth[], key: "expense" | "debt"): TrackCategory[] {
  return cats.flatMap((c) => {
    if (isDebtName(c.name)) return key === "debt" ? [{ id: c.id, name: c.name, planned: c.effective_limit, actual: c.actual_month_amount, concepts: concepts(b, c) }] : [];
    const dp = sumItems(b, c, "amount"), da = sumItems(b, c, "actual_month_amount");
    if (key === "debt") return dp > 0 || da > 0 ? [{ id: `${c.id}-debt`, name: c.name, planned: dp, actual: da, concepts: concepts(b, c, "debt") }] : [];
    const row = { id: c.id, name: c.name, planned: r2(c.effective_limit - dp), actual: r2(c.actual_month_amount - da), concepts: concepts(b, c, "rest") };
    return row.planned > 0.005 || Math.abs(row.actual) > 0.005 || row.concepts.length ? [row] : [];
  });
}

export function buildTracking(b: BudgetResponse): TrackSection[] {
  const rows = b.groups.flatMap((g) => g.categories).concat(b.income_categories.filter((c) => !b.groups.some((g) => g.categories.includes(c))));
  const unique = Array.from(new Map(rows.map((c) => [c.id, c])).values());
  return (Object.keys(META) as TrackKey[]).map((key) => {
    const m = META[key];
    const active = unique.filter((c) => c.type === m.type
      && (c.effective_limit > 0 || Math.abs(c.actual_month_amount) > 0.005 || b.items.some((i) => i.category_id === c.id)));
    const cats = key === "expense" || key === "debt" ? spendCats(b, active, key) : active
      .map((c) => ({ id: c.id, name: c.name, planned: c.effective_limit, actual: c.actual_month_amount, concepts: concepts(b, c),
        flow: key === "savings" && b.savings_flow ? b.savings_flow : undefined }));
    const flow = key === "savings" && b.savings_flow ? b.savings_flow : undefined;
    return { key, ...m, cats, flow, planned: r2(cats.reduce((s, c) => s + c.planned, 0)), actual: r2(cats.reduce((s, c) => s + c.actual, 0)) };
  });
}

export const difOf = (key: TrackKey, planned: number, actual: number) => (isSpend(key) ? planned - actual : actual - planned);

export function restTone(key: TrackKey, planned: number, actual: number): string {
  const dif = difOf(key, planned, actual);
  return dif > 0.005 ? "text-[#1B6E4F]" : dif < -0.005 ? "text-[#B23A1E]" : "text-slate-600";
}

function progressTone(key: TrackKey, planned: number, actual: number): string {
  if (!isSpend(key)) return actual >= planned && planned > 0 ? "text-[#1B6E4F]" : "text-slate-600";
  if (actual > planned + 0.005) return "text-[#B23A1E]";
  if (planned > 0 && actual / planned >= 0.9) return "text-[#B45309]";
  return "text-[#1B6E4F]";
}

export function barTone(key: TrackKey, planned: number, actual: number): string {
  return progressTone(key, planned, actual).replace("text-", "bg-").replace("bg-slate-600", "bg-[#1B2A4A]");
}
